import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const flagEmojis = {
  TH: "\uD83C\uDDF9\uD83C\uDDED",
  EU: "\uD83C\uDDEA\uD83C\uDDFA",
  ID: "\uD83C\uDDEE\uD83C\uDDE9",
  PH: "\uD83C\uDDF5\uD83C\uDDED",
  CO: "\uD83C\uDDE8\uD83C\uDDF4",
  CR: "\uD83C\uDDE8\uD83C\uDDF7",
  AU: "\uD83C\uDDE6\uD83C\uDDFA",
  JP: "\uD83C\uDDEF\uD83C\uDDF5",
  GB: "\uD83C\uDDEC\uD83C\uDDE7",
  US: "\uD83C\uDDFA\uD83C\uDDF8",
};

const strictnessColors = {
  high: { bg: "bg-red-50", text: "text-red-600", border: "border-red-200", dot: "bg-red-500" },
  medium: { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200", dot: "bg-yellow-500" },
  low: { bg: "bg-green-50", text: "text-green-600", border: "border-green-200", dot: "bg-green-500" },
};

export default function CountryGuides({ data }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClick = (guide) => {
    const slug = guide.country_name?.toLowerCase().replace(/\s+/g, "-");
    if (slug) navigate(`/guides/${slug}`);
  };

  return (
    <section id="country-guides" className="py-24 md:py-32 bg-white" data-testid="country-guides-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-[#1e3a5f]">
            {t("country_guides.title")}
          </h2>
        </motion.div>

        {!data ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="border border-[#e2e8f0] rounded-2xl p-5">
                <Skeleton className="h-10 w-10 rounded-full mb-3" />
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="country-guides-grid">
            {data.map((guide, i) => {
              const sc = strictnessColors[guide.strictness_level] || strictnessColors.medium;
              return (
                <motion.div
                  key={guide.country_code}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  onClick={() => handleClick(guide)}
                  className="bg-white border border-[#e2e8f0] rounded-2xl p-5 hover:shadow-md hover:scale-[1.03] transition-all duration-300 cursor-pointer group"
                  data-testid={`country-guide-${guide.country_code}`}
                >
                  <div className="text-3xl mb-3">
                    {flagEmojis[guide.country_code] || guide.flag_emoji}
                  </div>
                  <h3 className="font-['Outfit'] text-sm font-semibold text-[#1e3a5f] mb-2">
                    {guide.country_name}
                  </h3>
                  <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${sc.bg} ${sc.text} ${sc.border} border`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                    {t(`country_guides.${guide.strictness_level}`)}
                  </div>
                  <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs text-[#f97316] font-medium flex items-center gap-1">
                      {t("country_guides.learn_more")}
                      <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
